import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { useToast } from '../../context/ToastContext';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'rich_text', label: 'Rich Text' },
  { value: 'url', label: 'URL' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'multi_select', label: 'Multi Select' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object_relationship', label: 'Relationship' },
  { value: 'user', label: 'User' },
];

function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s_]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

export default function FieldDefinitionModal({
  objectDefinitionId,
  field,
  objectDefinitions,
  nextSortOrder,
  onClose,
  onSaved,
}) {
  const { showSuccess, showError } = useToast();
  const isEdit = !!field;

  const [form, setForm] = useState({
    name: '',
    slug: '',
    field_type: 'text',
    is_required: false,
    options: null,
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [choices, setChoices] = useState(['']);

  useEffect(() => {
    if (field) {
      setForm({
        name: field.name,
        slug: field.slug,
        field_type: field.field_type,
        is_required: field.is_required,
        options: field.options,
      });
      setSlugTouched(true);
      if ((field.field_type === 'select' || field.field_type === 'multi_select') && field.options?.choices) {
        setChoices(field.options.choices.length > 0 ? field.options.choices : ['']);
      }
    }
  }, [field]);

  const handleNameChange = (name) => {
    setForm((prev) => ({
      ...prev,
      name,
      slug: slugTouched ? prev.slug : slugify(name),
    }));
  };

  const handleFieldTypeChange = (field_type) => {
    setForm((prev) => ({ ...prev, field_type, options: null }));
    if (field_type === 'select' || field_type === 'multi_select') {
      setChoices(['']);
    }
  };

  const handleChoiceChange = (index, value) => {
    setChoices((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const addChoice = () => setChoices((prev) => [...prev, '']);

  const removeChoice = (index) => {
    setChoices((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    let options = form.options;
    if (form.field_type === 'select' || form.field_type === 'multi_select') {
      const filteredChoices = choices.filter((c) => c.trim() !== '');
      options = filteredChoices.length > 0 ? { choices: filteredChoices } : null;
    }

    const payload = {
      name: form.name,
      field_type: form.field_type,
      is_required: form.is_required,
      options,
    };

    try {
      let response;
      if (isEdit) {
        response = await apiRequest(`/crm/fields/${field.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        response = await apiRequest('/crm/fields/', {
          method: 'POST',
          body: JSON.stringify({
            ...payload,
            object_definition_id: objectDefinitionId,
            slug: form.slug,
            sort_order: nextSortOrder,
          }),
        });
      }

      if (response.ok) {
        showSuccess(isEdit ? 'Field updated' : 'Field created');
        onSaved();
      } else {
        const err = await response.json();
        showError(err.detail || `Failed to ${isEdit ? 'update' : 'create'} field`);
      }
    } catch (error) {
      showError(`Error ${isEdit ? 'updating' : 'creating'} field`);
    } finally {
      setSaving(false);
    }
  };

  const showChoicesEditor = form.field_type === 'select' || form.field_type === 'multi_select';
  const showRelationshipPicker = form.field_type === 'object_relationship';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative z-10 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {isEdit ? 'Edit Field' : 'Add Field'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Field Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
              placeholder="e.g. Revenue, Status, Priority"
              required
              autoFocus={!isEdit}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Slug
            </label>
            {isEdit ? (
              <div className="px-3.5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm text-gray-500 dark:text-gray-400 font-mono">
                {form.slug}
              </div>
            ) : (
              <input
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((prev) => ({ ...prev, slug: slugify(e.target.value) }));
                }}
                className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 font-mono transition-colors"
                placeholder="auto_generated_slug"
                required
              />
            )}
          </div>

          {/* Field Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Field Type
            </label>
            {isEdit ? (
              <div className="px-3.5 py-2.5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-sm text-gray-500 dark:text-gray-400">
                {FIELD_TYPES.find((t) => t.value === form.field_type)?.label || form.field_type}
              </div>
            ) : (
              <select
                value={form.field_type}
                onChange={(e) => handleFieldTypeChange(e.target.value)}
                className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Required toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Required</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Records must have a value for this field
              </p>
            </div>
            <button
              type="button"
              aria-pressed={form.is_required}
              onClick={() => setForm((prev) => ({ ...prev, is_required: !prev.is_required }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                form.is_required ? 'bg-brand-600' : 'bg-gray-200 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                  form.is_required ? 'translate-x-5' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Select/Multi-select choices */}
          {showChoicesEditor && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Options
              </label>
              <div className="space-y-2">
                {choices.map((choice, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={choice}
                      onChange={(e) => handleChoiceChange(index, e.target.value)}
                      className="flex-1 px-3.5 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
                      placeholder={`Option ${index + 1}`}
                    />
                    {choices.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeChoice(index)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addChoice}
                  className="text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300 font-medium flex items-center gap-1 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add option
                </button>
              </div>
            </div>
          )}

          {/* Relationship picker */}
          {showRelationshipPicker && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Related Object
              </label>
              <select
                value={form.options?.related_object_definition_id || ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    options: { ...prev.options, related_object_definition_id: e.target.value },
                  }))
                }
                className="block w-full px-3.5 py-2.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors"
              >
                <option value="">Select an object...</option>
                {objectDefinitions?.map((od) => (
                  <option key={od.id} value={od.id}>
                    {od.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.name.trim() || (!isEdit && !form.slug.trim())}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (isEdit ? 'Saving...' : 'Creating...') : isEdit ? 'Save Changes' : 'Create Field'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
